import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup, Row, Col, InputGroup, Badge, Spinner } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputMask from 'react-input-mask'; // 1. Importar o InputMask
import axios from 'axios'; // 2. Importar axios 

const LeadModal = ({ show, onHide, lead, onSave, colaboradores, currentUser, isSaving, showAlertModal, onOpenScheduleModal }) => {
    const [formData, setFormData] = useState({});
    const [newNote, setNewNote] = useState('');
    // --- NOVOS ESTADOS PARA TAREFAS ---
    const [tasks, setTasks] = useState([]);
    const [score, setScore] = useState(0); // NOVO: Estado para a pontuação
    const [newTaskDescription, setNewTaskDescription] = useState('');
    const [newTaskDueDate, setNewTaskDueDate] = useState('');
    const [isConverting, setIsConverting] = useState(false);

    useEffect(() => {
        if (lead && show) { // Garante que o estado seja atualizado apenas quando o modal for exibido com um lead
            // Editando um lead existente
            setFormData({ ...lead });
            setTasks(lead.tarefas || []); // Carrega as tarefas do lead
            setScore(lead.pontuacao || 0); // Carrega a pontuação do lead
        } else {
            // Criando um novo lead
            setFormData({
                nome: '',
                telefone: '',
                email: '',
                prioridade: 'baixa',
                status: 'novo',
                historicos: [],
                origem_contato: null, // Valor inicial para novo lead
                respondeu_contato: false, // Valor inicial para novo lead
                tarefas: [],
                responsavel_id: '', // Campo para o responsável
            });
            setTasks([]); // Limpa as tarefas
            setScore(0); // Reseta a pontuação
        }
        setNewNote(''); // Limpa a nota ao abrir o modal
        resetNewTaskFields(); // Limpa os campos da nova tarefa
    }, [lead, show]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetNewTaskFields = () => {
        setNewTaskDescription('');
        setNewTaskDueDate('');
    };

    const handleAddTask = () => {
        if (!newTaskDescription || !newTaskDueDate) {
            showAlertModal('Campo Obrigatório', 'Por favor, preencha a descrição e a data da tarefa.', 'warning');
            return;
        }
        const newTask = {
            // Usamos um ID temporário para o React. O backend irá gerar o ID real.
            id: `temp-${Date.now()}`,
            descricao: newTaskDescription,
            data_vencimento: newTaskDueDate,
            concluida: 0,
            user_id: currentUser.id, // Associa o usuário logado
            user: { nome: currentUser.nome } // Exibe o nome imediatamente
        };
        setTasks(prevTasks => [...prevTasks, newTask]);
        resetNewTaskFields();
    };

    const handleToggleTask = (taskId) => {
        setTasks(prevTasks =>
            prevTasks.map(task =>
                task.id === taskId ? { ...task, concluida: task.concluida ? 0 : 1 } : task
            )
        );
    };

    const handleDeleteTask = (taskId) => {
        // Se a tarefa tiver um ID temporário, apenas removemos do estado.
        if (String(taskId).startsWith('temp-')) {
            setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
        } else {
            // Se for uma tarefa existente, marcamos para exclusão.
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task.id === taskId ? { ...task, _delete: true } : task
                )
            );
        }
    };

    const handleSave = () => {
        // Validação do Telefone: se preenchido, não pode estar incompleto.
        if (formData.telefone && formData.telefone.includes('_')) {
            showAlertModal('Validação', 'O campo Telefone está incompleto. Por favor, preencha-o corretamente ou deixe-o em branco.', 'warning');
            return; // Impede o salvamento
        }

        // Validação do Email: se preenchido, deve ter um formato válido.
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            showAlertModal('Validação', 'O formato do Email é inválido. Por favor, corrija-o ou deixe o campo em branco.', 'warning');
            return; // Impede o salvamento
        }

        // Se as validações passaram, prossegue com o salvamento.
        const dataToSave = {
            ...formData,
            nova_nota: newNote,
            pontuacao: score, // Adiciona a pontuação ao payload
            tarefas: tasks, // Inclui o array de tarefas (com as novas, atualizadas e marcadas para exclusão)
        };
        onSave(dataToSave);
    };

    const handleSchedule = async () => {
        if (!lead || !lead.id) return;

        setIsConverting(true);
        try {
            // 1. Chama a API para converter o lead em paciente
            const response = await axios.post(`/api/leads/${lead.id}/convert-to-patient`);
            const paciente = response.data.paciente;

            // 3. Abre o novo modal de agendamento
            onOpenScheduleModal(paciente);

            // 2. Fecha o modal de lead APÓS o de agendamento ser aberto
            onHide();
        } catch (error) {
            // 1. Verifica se o erro é o 409 Conflict que configuramos
            if (error.response && error.response.status === 409) {
                // 2. Exibe a mensagem específica vinda do backend
                showAlertModal('Conflito de E-mail', error.response.data.message, 'warning');
            } else {
                showAlertModal('Erro', 'Não foi possível converter o lead em paciente para agendamento.', 'danger');
            }
            console.error("Erro ao converter lead:", error);
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop={isSaving ? 'static' : true} keyboard={!isSaving}>
            {/* Overlay de Carregamento */}
            {isSaving && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1056, // z-index maior que o do conteúdo do modal
                    borderRadius: 'var(--bs-modal-border-radius, 0.5rem)'
                }}>
                    <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                    <h5 className="mt-3">Salvando dados...</h5>
                    <p className="text-muted">Por favor, aguarde.</p>
                </div>
            )}
            <Modal.Header closeButton={!isSaving}>
                <Modal.Title>{lead ? 'Detalhes do Lead' : 'Novo Lead'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="leadNome">
                                <Form.Label>Nome</Form.Label>
                                <Form.Control type="text" name="nome" value={formData.nome || ''} onChange={handleChange} />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="leadTelefone">
                                <Form.Label>Telefone</Form.Label>
                                {/* 2. Substituir Form.Control por InputMask para o telefone */}
                                <InputMask
                                    mask="(99) 99999-9999"
                                    value={formData.telefone || ''}
                                    onChange={handleChange}
                                >
                                    {(inputProps) => <Form.Control {...inputProps} type="tel" name="telefone" />}
                                </InputMask>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="leadEmail">
                                <Form.Label>Email</Form.Label>                                
                                {/* 3. Adicionar uma máscara simples para o email */}
                                <Form.Control type="email" name="email" value={formData.email || ''} onChange={handleChange} placeholder="exemplo@dominio.com" />

                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="leadPrioridade">
                                <Form.Label>Prioridade</Form.Label>
                                <Form.Select name="prioridade" value={formData.prioridade || 'baixa'} onChange={handleChange}>
                                    <option value="baixa">Baixa</option>
                                    <option value="media">Média</option>
                                    <option value="alta">Alta</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* --- NOVA SEÇÃO DE PONTUAÇÃO --- */}
                    <Row>
                        <Col>
                            <Form.Group className="mb-3" controlId="leadPontuacao">
                                <Form.Label>Pontuação do Lead</Form.Label>
                                <div>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <FontAwesomeIcon
                                            key={star}
                                            icon={star <= score ? 'star' : ['far', 'star']} // Usa ícone sólido ou regular
                                            onClick={() => setScore(star === score ? 0 : star)} // Clicar de novo na mesma estrela zera a pontuação
                                            style={{ cursor: 'pointer', color: '#ffc107', marginRight: '5px', fontSize: '1.5rem' }}
                                        />
                                    ))}
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>
                    {/* --- NOVOS CAMPOS DE ORIGEM E RESPOSTA --- */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="leadOrigemContato">
                                <Form.Label>Origem do Contato</Form.Label>
                                <Form.Select name="origem_contato" value={formData.origem_contato || ''} onChange={handleChange}>
                                    <option value="">Não especificado</option>
                                    <option value="clinica_iniciou">Clínica iniciou</option>
                                    <option value="lead_iniciou">Lead iniciou</option>
                                    <option value="indicacao">Veio por indicação</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6} className="d-flex align-items-center mt-3">
                            <Form.Group className="mb-3" controlId="leadRespondeuContato">
                                <Form.Check 
                                    type="switch"
                                    id="respondeu-contato-switch"
                                    label="Lead já respondeu ao contato?"
                                    name="respondeu_contato"
                                    checked={!!formData.respondeu_contato}
                                    onChange={(e) => setFormData(prev => ({ ...prev, respondeu_contato: e.target.checked }))}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Group className="mb-3" controlId="leadResponsavel">
                                <Form.Label>Responsável</Form.Label>
                                <Form.Select name="responsavel_id" value={formData.responsavel_id || ''} onChange={handleChange}>
                                    <option value="">Não atribuído</option>
                                    {colaboradores.map(colaborador => (
                                        <option key={colaborador.id} value={colaborador.id}>{colaborador.nome}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>

                <hr />

                {/* --- SEÇÃO DE TAREFAS --- */}
                <h5><FontAwesomeIcon icon="tasks" className="me-2" /> Próximas Ações</h5>
                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Descreva a próxima tarefa..."
                        value={newTaskDescription}
                        onChange={(e) => setNewTaskDescription(e.target.value)}
                    />
                    <Form.Control
                        type="datetime-local"
                        value={newTaskDueDate}
                        onChange={(e) => setNewTaskDueDate(e.target.value)}
                        style={{ maxWidth: '250px' }}
                    />
                    <Button variant="outline-primary" onClick={handleAddTask}>Adicionar</Button>
                </InputGroup>

                <ListGroup variant="flush" className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {tasks.filter(task => !task._delete).map((task) => (
                        <ListGroup.Item key={task.id} className={`px-0 d-flex align-items-center ${task.concluida ? 'text-muted text-decoration-line-through' : ''}`}>
                            <Form.Check
                                type="checkbox"
                                id={`task-${task.id}`}
                                checked={!!task.concluida}
                                onChange={() => handleToggleTask(task.id)}
                                className="me-3"
                            />
                            <div className="flex-grow-1">
                                {task.descricao}
                                <div className="d-flex justify-content-between">
                                    <small>
                                        <FontAwesomeIcon icon="user" className="me-1" /> {task.user?.nome || '...'}
                                    </small>
                                    <small>
                                        <FontAwesomeIcon icon="calendar-alt" className="me-1" />
                                        {format(parseISO(task.data_vencimento), "dd/MM/yyyy HH:mm")}
                                    </small>
                                </div>
                            </div>
                            <Button variant="outline-danger" size="sm" className="ms-3" onClick={() => handleDeleteTask(task.id)}>
                                <FontAwesomeIcon icon="trash" />
                            </Button>
                        </ListGroup.Item>
                    ))}
                </ListGroup>

                {/* --- SEÇÃO DE HISTÓRICO --- */}
                <h5>Histórico de Contatos</h5>
                <ListGroup variant="flush" className="mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {(formData.historicos || []).map((item) => (
                        <ListGroup.Item key={item.id} className="px-0">
                            <div className="d-flex justify-content-between">
                                <small className="fw-bold">{item.user?.nome || 'Sistema'}</small>
                                <small className="text-muted">
                                    {format(parseISO(item.created_at), "dd/MM/yyyy 'às' HH:mm")}
                                </small>
                            </div>
                            <p className="mb-0">{item.nota}</p>
                        </ListGroup.Item>
                    ))}
                </ListGroup>

                <InputGroup>
                    <Form.Control as="textarea" rows={2} placeholder="Adicionar nova anotação..." value={newNote} onChange={(e) => setNewNote(e.target.value)} />
                </InputGroup>

            </Modal.Body>
            <Modal.Footer>
                {/* 1. Botão de Agendar/Reagendar */}
                {lead && lead.status !== 'convertido' && (
                    <Button variant="info" onClick={handleSchedule} disabled={isSaving || isConverting} title="Converte o lead em paciente e abre a agenda.">
                        {isConverting ? (
                            <><Spinner as="span" size="sm" /> Convertendo...</>
                        ) : (
                            <><FontAwesomeIcon icon="calendar-alt" className="me-2" /> Agendar</>
                        )}
                    </Button>
                )}
                {lead && lead.status === 'convertido' && (
                    <Button variant="warning" onClick={handleSchedule} disabled={isSaving || isConverting} title="Abre a agenda para marcar uma nova consulta para este paciente.">
                        {isConverting ? (
                            <><Spinner as="span" size="sm" /> Carregando...</>
                        ) : (
                            <><FontAwesomeIcon icon="calendar-check" className="me-2" /> Reagendar</>
                        )}
                    </Button>
                )}

                {/* 2. Botões de Cancelar e Salvar */}
                <Button variant="secondary" onClick={onHide} disabled={isSaving}>Cancelar</Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className="ms-2">Salvando...</span>
                        </>
                    ) : 'Salvar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LeadModal;
